import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { zonedInstant } from '@core/helpers/slot-series.helper';
import {
  BindingCandidates,
  ChannelType,
  ChannelUsageEntry,
  DataSourceDevice,
  MeasurementPoint,
  MeasurementPointDetail,
  Source,
  UnboundChannel,
} from '@core/models/installation.model';
import { SetBinding } from '@core/models/installation-write.model';
import { InstallationAttributesService } from '@core/services/installation-attributes.service';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SpinnerComponent,
  TagComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of, throwError } from 'rxjs';
import { channelFields } from '../../channels';
import { PlacedRefusal, RefusalPlace, placeRefusal } from '../../editor-refusal';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';
import { EffectiveFromFieldComponent } from '../effective-from-field/effective-from-field.component';
import {
  ChannelRow,
  DeviceChannels,
  StatusTag,
  deviceChannels,
  rowKey,
  statusTag,
} from './binding-channels';

export type BindingMode = 'bind' | 'change';

export interface BindingDialogData {
  installationOid: string;
  pointOid: string;
  mode: BindingMode;
  // The zone the instant is entered in.
  timeZone: string;
  // To name the points that hold a channel, and the devices when nothing here matches.
  points: MeasurementPoint[];
  devices: DataSourceDevice[];
  // The free channels of those devices; the held ones are read per device.
  unboundChannels: UnboundChannel[];
}

// What stands between the point and a single obvious channel, said once above the channels.
type Note = 'none' | 'several' | 'shared' | 'inUse';

export interface ChannelCard {
  name: string;
  unit: string | null;
  device: string;
}

interface Loaded {
  candidates: BindingCandidates;
  channelTypes: ChannelType[];
  usages: ChannelUsageEntry[];
}

// The channel table needs more than the candidates; when one of those reads fails the dialog offers
// to load again rather than show a list with holes in it.
const SUPPLEMENT_FAILED = new Error('channel supplement failed');

// Closes with the point as written, or with nothing when cancelled.
@Component({
  selector: 'xemex-binding-dialog',
  imports: [
    DialogShellComponent,
    EffectiveFromFieldComponent,
    FilledButtonComponent,
    MatIconModule,
    MatRadioModule,
    NgTemplateOutlet,
    SpinnerComponent,
    TagComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './binding-dialog.component.html',
  styleUrl: './binding-dialog.component.scss',
})
export class BindingDialogComponent {
  readonly data = inject<BindingDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<BindingDialogComponent, MeasurementPointDetail>>(MatDialogRef);
  private readonly service = inject(InstallationDataSourcesService);
  private readonly attributes = inject(InstallationAttributesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly point = this.data.points.find(point => point.pointOid === this.data.pointOid) ?? null;
  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly candidates = signal<BindingCandidates | null>(null);
  private readonly channelTypes = signal<ChannelType[] | null>(null);
  private readonly usages = signal<ChannelUsageEntry[]>([]);
  readonly selectedKey = signal<string | null>(null);
  readonly shownDeviceId = signal<number | null>(null);
  readonly instant = signal(new Date().toISOString());
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  private readonly refusedKey = signal<string | null>(null);
  // Read by the opener once the dialog closes: the point or the installation changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  readonly category = this.point ? roleGroupKey(this.point.role) : 'other';
  readonly categoryIcon = ROLE_GROUP_ICONS[this.category];
  private readonly binding = this.point?.binding ?? null;
  readonly linkedSince = this.binding
    ? zonedInstant(this.binding.validFrom, this.data.timeZone, 'D MMM YYYY')
    : null;
  readonly startedAt = this.binding
    ? zonedInstant(this.binding.validFrom, this.data.timeZone, 'D MMM YYYY HH:mm')
    : null;

  readonly devices = computed<DeviceChannels[]>(() => {
    const found = this.candidates();
    if (found === null) return [];
    return deviceChannels({
      installationOid: this.data.installationOid,
      point: this.point,
      points: this.data.points,
      devices: this.data.devices,
      candidates: found.candidates,
      unbound: this.data.unboundChannels,
      usages: this.usages(),
      channelTypes: this.channelTypes() ?? [],
    });
  });
  readonly rows = computed<ChannelRow[]>(() => this.devices().flatMap(device => device.rows));
  readonly shownDevice = computed(
    () =>
      this.devices().find(device => device.device.deviceId === this.shownDeviceId()) ??
      this.devices()[0] ??
      null,
  );

  readonly shownRows = computed<{ row: ChannelRow; tag: StatusTag }[]>(() =>
    (this.shownDevice()?.rows ?? []).map(row => ({ row, tag: statusTag(row) })),
  );

  readonly note = computed<Note | null>(() => {
    const found = this.candidates();
    if (found === null) return null;
    if (found.candidates.length === 0) return 'none';
    if (this.data.mode === 'change') return null;
    switch (found.ambiguity) {
      case 'several_candidates':
        return 'several';
      case 'shared_candidate':
        return 'shared';
      case 'channel_in_use':
        return 'inUse';
      default:
        return null;
    }
  });
  readonly hasCandidates = computed(() => (this.candidates()?.candidates.length ?? 0) > 0);

  readonly deviceNames = this.data.devices
    .map(device => device.ldn ?? String(device.deviceId))
    .join(', ');

  private readonly selected = computed(
    () => this.rows().find(row => row.key === this.selectedKey() && row.selectable) ?? null,
  );
  readonly currentCard = computed<ChannelCard | null>(() =>
    this.binding ? this.card(this.binding.source) : null,
  );
  readonly newCard = computed<ChannelCard | null>(() => {
    const row = this.selected();
    return row ? this.card(row.source) : null;
  });
  // The one part of the rule the dialog can check itself: a change starts after the current link.
  readonly instantBroken = computed(
    () => this.binding !== null && Date.parse(this.instant()) <= Date.parse(this.binding.validFrom),
  );
  readonly canSave = computed(
    () =>
      this.selected() !== null &&
      !this.instantBroken() &&
      !this.isSaving() &&
      !this.isLoading() &&
      !this.stale(),
  );

  constructor() {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.reads()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: loaded => {
          this.channelTypes.set(loaded.channelTypes);
          this.usages.set(loaded.usages);
          this.candidates.set(loaded.candidates);
          this.isLoading.set(false);
          this.preselect(loaded.candidates);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const refusal = toRefusal(error);
          if (error !== SUPPLEMENT_FAILED && refusal.status === 404) {
            this.refusal.set(placeRefusal(refusal));
          } else {
            this.loadFailed.set(true);
          }
        },
      });
  }

  select(key: string): void {
    this.selectedKey.set(key);
  }

  showDevice(deviceId: number): void {
    this.shownDeviceId.set(deviceId);
  }

  messageAt(place: RefusalPlace): string | null {
    const refusal = this.refusal();
    return refusal?.place === place ? refusal.messageKey : null;
  }

  candidateError(key: string): string | null {
    return key === this.refusedKey() ? this.messageAt('candidate') : null;
  }

  save(): void {
    const row = this.selected();
    const channel = row ? channelFields(row.source) : null;
    if (!row || !channel) return;
    const body: SetBinding = {
      source_kind: 'device_channel',
      effective_from: this.instant(),
      ...channel,
    };
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .setBinding(this.data.installationOid, this.data.pointOid, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => this.dialogRef.close(detail),
        error: (error: unknown) => {
          this.isSaving.set(false);
          const refusal = placeRefusal(toRefusal(error));
          this.refusal.set(refusal);
          this.refusedKey.set(row.key);
          // The refusal does not name the holder; the candidates read again do.
          if (refusal.place === 'candidates') this.load();
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private reads(): Observable<Loaded> {
    const supplement = <T>(read: Observable<T>): Observable<T> =>
      read.pipe(catchError(() => throwError(() => SUPPLEMENT_FAILED)));
    const known = this.channelTypes();
    return forkJoin({
      candidates: this.service.candidates(this.data.installationOid, this.data.pointOid),
      channelTypes: known
        ? of(known)
        : supplement(this.service.channelVocabulary().pipe(map(found => found.channelTypes ?? []))),
      usages: this.data.devices.length
        ? supplement(
            forkJoin(
              this.data.devices.map(device => this.attributes.channelUsage(device.deviceId)),
            ),
          ).pipe(map(usage => usage.flatMap(one => one.usages ?? [])))
        : of([]),
    });
  }

  private card(source: Source): ChannelCard {
    const device = this.data.devices.find(one => one.deviceId === source.deviceId);
    const row = this.rows().find(one => one.key === rowKey(source));
    const unit =
      row?.unit ??
      this.channelTypes()?.find(
        type => type.contextType === source.contextType && type.valueName === source.valueName,
      )?.unit ??
      null;
    return {
      name: [source.valueName, row?.context].filter(Boolean).join(' · '),
      unit,
      device: [device?.deviceTypeName ?? device?.protocol, source.ldn ?? source.serial]
        .filter(Boolean)
        .join(' · '),
    };
  }

  // Without ambiguity binding is a confirmation, so the one free channel is already chosen. A
  // channel another point holds can still be listed beside it, with its holder. The device shown
  // first is the one the point reads now, else the first with a channel to choose.
  private preselect(found: BindingCandidates): void {
    if (this.shownDeviceId() === null) {
      const devices = this.devices();
      const current = this.binding?.source.deviceId;
      const shown =
        devices.find(device => device.device.deviceId === current) ??
        devices.find(device => device.available > 0) ??
        devices[0];
      if (shown) this.showDevice(shown.device.deviceId);
    }
    if (this.selectedKey() !== null) return;
    if (this.data.mode !== 'bind' || found.ambiguity !== 'none') return;
    const free = found.candidates.filter(candidate => !candidate.inUse);
    if (free.length === 1) {
      this.select(rowKey(free[0].source));
    }
  }
}
